import { Close } from "@mui/icons-material";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useGetTagsBasedOnCanonicalNames } from "../../hooks";
import { SelectBoxOption } from "../../interfaces";
import { useLazyGetTagGroupsQuery } from "../../redux/services";
import { AddEditTags } from "../../screens/tags/tagListing/addEditTags";
import { TAG_CANONICAL_NAMES } from "../../utils";
import { HighlightSelectBox, HighlightSelectProps } from "../highLightSelectBox";

export type CanonicalKeys = keyof typeof TAG_CANONICAL_NAMES;

interface ITagsHighlightSelect extends Omit<HighlightSelectProps, 'label'> {
    id: string;
    canonical_name: CanonicalKeys;
    hideLabel?: boolean;
    options?: SelectBoxOption[];
}

export const TagsHighlightSelect: React.FC<ITagsHighlightSelect> = (
    {
        id,
        canonical_name,
        options,
        hideLabel = false,
        ...rest
    }
) => {
    const { isLoading, isFetching, options: fetchedOptions, refetch, isSuccess } = useGetTagsBasedOnCanonicalNames(canonical_name);

    const [getTagGroupBasedOnCanonicalName, { data: allData = { data: [], totalCount: 0 } }] = useLazyGetTagGroupsQuery();

    const { data } = allData;

    const loading = isLoading || isFetching;

    const finalizedOptions = options || fetchedOptions || data;

    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);

    const handleClose = (newTag?: any) => {
        if (newTag) {
            rest?.onChange(newTag);
        }
        setOpen(false)
    };

    const selectedTagGroup = finalizedOptions[0] ? { label: finalizedOptions[0].tag_group_name, value: finalizedOptions[0].tag_group_id ?? finalizedOptions[0]._id, _id: finalizedOptions[0].tag_group_id ?? finalizedOptions[0]._id } : undefined;

    useEffect(() => {
        if (canonical_name && (options || fetchedOptions).length === 0 && isSuccess) { 
            getTagGroupBasedOnCanonicalName({ canonical_name }); 
        }
        // eslint-disable-next-line
    }, [canonical_name, isSuccess]);

    return (
        <>
            <HighlightSelectBox
                {...rest}
                id={id}
                label={loading ? "Loading..." : hideLabel ? "" : selectedTagGroup?.label ?? canonical_name}
                options={finalizedOptions}
                loading={loading}
                // titleIcon={<Tooltip title={`Create a tag for ${selectedTagGroup?.label ?? canonical_name}`}>
                //     <Button
                //         color="primary"
                //         onClick={handleOpen}
                //         size="small"
                //         variant="outlined"
                //         sx={{ px: 0, minWidth: 'unset', width: 24, height: 24 }}
                //     >
                //         <Add htmlColor="primary" sx={{ fontSize: 14 }} />
                //     </Button>
                // </Tooltip>}
                newOptionCreationProps={{
                    title: "Create New Tag",
                    onAddBtnClick: handleOpen
                }}
            />

            <Dialog
                key={JSON.stringify(rest?.value)}
                maxWidth={"sm"}
                fullWidth={true}
                open={open}
                onClose={() => handleClose()}
            >
                <>
                    {/* Dialog Title */}
                    <DialogTitle sx={{ textAlign: 'center' }}>Create Tag</DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => handleClose()}
                        sx={{
                            backgroundColor: "#fff",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            outline: "none",
                            transition: "box-shadow 0.3s ease-in-out",
                            ":hover, :focus": {
                                boxShadow: "0px 0px 5px 2px rgba(103, 58, 183, 0.5)",
                            },
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                            width: "25px",
                            height: "25px",
                            marginTop: "10px",
                            marginRight: "11px",
                        }}
                    >
                        <Close />
                    </IconButton>
                </>
                <DialogContent>
                    <AddEditTags isEdit={false} data={null} refetch={refetch} selectedTagGroup={selectedTagGroup} onDialogClose={(newTag) => handleClose(rest?.multiple ? [...(rest?.value as SelectBoxOption[] ?? []), newTag].filter((_) => _?.value) : newTag)} />
                </DialogContent>
            </Dialog>
        </>
    )
}
